import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
} from "react";

// ─── Constants ───────────────────────────────────────────────────────────────

const STORAGE_KEY = "datatool_ui_version";

// ─── Types ────────────────────────────────────────────────────────────────────

export type UiVersion = "legacy" | "new";

interface ThemeVersionContextProps {
  uiVersion: UiVersion;
  toggleUiVersion: () => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const ThemeVersionContext = createContext<ThemeVersionContextProps | undefined>(
  undefined
);

// ─── Provider ─────────────────────────────────────────────────────────────────

interface ThemeVersionProviderProps {
  children: ReactNode;
}

/**
 * ThemeVersionProvider — DQ-8526
 *
 * Manages the global UI version toggle ("legacy" | "new").
 * Reads the persisted value from localStorage on mount and defaults to
 * "legacy" so existing users see zero visual change on first deploy.
 *
 * Place this provider inside AuthProvider (and outside ThemeProvider) so
 * that the active version is available everywhere in the React tree before
 * the MUI ThemeProvider selects which theme to apply.
 */
export const ThemeVersionProvider: React.FC<ThemeVersionProviderProps> = ({
  children,
}) => {
  const [uiVersion, setUiVersion] = useState<UiVersion>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === "new" ? "new" : "legacy";
  });

  const toggleUiVersion = () => {
    setUiVersion((prev) => {
      const next: UiVersion = prev === "legacy" ? "new" : "legacy";
      localStorage.setItem(STORAGE_KEY, next);
      return next;
    });
  };

  return (
    <ThemeVersionContext.Provider value={{ uiVersion, toggleUiVersion }}>
      {children}
    </ThemeVersionContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * useThemeVersion — consume the active UI version and its toggle.
 *
 * Throws a descriptive error when called outside of ThemeVersionProvider
 * so misconfiguration is caught at development time.
 */
export const useThemeVersion = (): ThemeVersionContextProps => {
  const context = useContext(ThemeVersionContext);
  if (!context) {
    throw new Error(
      "useThemeVersion must be used within a ThemeVersionProvider. " +
        "Wrap your component tree with <ThemeVersionProvider>."
    );
  }
  return context;
};
